import { useCallback, useState } from "react";

import type { CreateCacheRequest } from "../../../types";

export interface CacheFormState {
  name: string;
  persistence: "none" | "aof" | "rdb";
  ha: "single" | "sentinel_3";
  storageSizeGi: number;
}

export const INITIAL_CACHE_STATE: CacheFormState = {
  name: "",
  persistence: "none",
  ha: "single",
  storageSizeGi: 5,
};

export interface CacheFormErrors {
  name?: string;
}

const POSTGRES_IDENTIFIER_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export function useCacheForm() {
  const [state, setState] = useState<CacheFormState>(INITIAL_CACHE_STATE);
  const [errors, setErrors] = useState<CacheFormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = useCallback((field: string, value: string) => {
    if (field === "name") {
      if (!value.trim()) return "Cache name is required";
      if (!POSTGRES_IDENTIFIER_RE.test(value))
        return "Start with a letter or underscore; use only letters, numbers, and underscores. Hyphens are not allowed. Example: test_pg_db (not test-pg-db)";
      if (value.length > 50) return "Name must be 50 characters or less";
    }
    return undefined;
  }, []);

  const setField = useCallback(
    <K extends keyof CacheFormState>(field: K, value: CacheFormState[K]) => {
      setState((s) => ({ ...s, [field]: value }));
      if (touched[field as string]) {
        const err = validateField(field as string, String(value));
        setErrors((e) => ({ ...e, [field as string]: err }));
      }
    },
    [touched, validateField],
  );

  const onBlur = useCallback(
    (field: string) => {
      setTouched((t) => ({ ...t, [field]: true }));
      const err = validateField(
        field,
        String(state[field as keyof CacheFormState] ?? ""),
      );
      setErrors((e) => ({ ...e, [field]: err }));
    },
    [state, validateField],
  );

  const validate = useCallback((): boolean => {
    const newErrors: CacheFormErrors = {};
    const nameErr = validateField("name", state.name);
    if (nameErr) newErrors.name = nameErr;
    setErrors(newErrors);
    setTouched({ name: true });
    return Object.keys(newErrors).length === 0;
  }, [state, validateField]);

  const buildRequest = useCallback((): CreateCacheRequest => {
    const req: CreateCacheRequest = { name: state.name };
    if (state.persistence !== "none") {
      req.persistence = state.persistence;
      req.storageSizeGi = state.storageSizeGi;
    }
    if (state.ha === "sentinel_3") req.ha = state.ha;
    return req;
  }, [state]);

  return { state, errors, setField, onBlur, validate, buildRequest };
}
